/**
 * ★ Fase 59.P2 — estratto da LeadDetailPage.jsx (split componenti oversize).
 * Comportamento e data-testid INVARIATI.
 */
import React, { useMemo } from 'react';
import i18n from '../../i18n';

const i18nCompleted = () => i18n.t('videoEvidence.completed', 'Completato (≥75%)');
import { Video } from 'lucide-react';
import { fmtDateTime, fmtRelative } from '../../lib/constants';

export function VideoEvidenceBlock({ lead }) {
  const cf = lead.custom_fields || {};
  const pctMax = Number(cf.video_progress_max || 0);
  const watchedAt = cf.video_watched_at;
  const firstSeenAt = cf.video_first_seen_at;
  const lastSeenAt = cf.video_last_seen_at;
  // ★ 2026-07-29 (hooks fix): compute + sort the milestones inside the memo
  // callback so we avoid the "conditional dep" warning about milestonesRaw.
  const sortedMilestones = useMemo(() => {
    const raw = Array.isArray(cf.video_milestones_timeline)
      ? cf.video_milestones_timeline
      : [];
    return raw.slice().sort((a, b) => (a.percent || 0) - (b.percent || 0));
  }, [cf.video_milestones_timeline]);

  // Niente dati → niente blocco
  if (!pctMax && sortedMilestones.length === 0 && !firstSeenAt) return null;

  const barColor = pctMax >= 100 ? 'bg-emerald-600'
    : pctMax >= 75 ? 'bg-emerald-500'
    : pctMax >= 50 ? 'bg-amber-500'
    : pctMax >= 25 ? 'bg-yellow-400'
    : 'bg-slate-300';
  const verdict = pctMax >= 75
    ? { label: '✅ Ha guardato il VSL (≥75%)', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' }
    : pctMax >= 25
      ? { label: '👀 Ha iniziato il VSL ma non l\'ha completato', cls: 'bg-amber-50 text-amber-800 border-amber-200' }
      : { label: '❌ Non ha guardato il VSL', cls: 'bg-slate-50 text-slate-700 border-slate-200' };

  return (
    <div className="space-y-2 text-xs" data-testid="video-evidence-block">
      <div className="flex items-center gap-2">
        <Video className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">VSL visto:</span>
        <span className="font-bold text-base text-foreground" data-testid="video-progress-max">
          {Math.round(pctMax)}%
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pctMax)}%` }} />
      </div>
      <div className={`mt-1 px-2 py-1.5 rounded text-[11px] font-semibold text-center border ${verdict.cls}`}>
        {verdict.label}
      </div>
      {(firstSeenAt || lastSeenAt) && (
        <div className="text-[10px] text-muted-foreground space-y-0.5 pt-1">
          {firstSeenAt && <div>Primo play: {fmtDateTime(firstSeenAt)}</div>}
          {lastSeenAt && <div>Ultima attività: {fmtRelative(lastSeenAt)}</div>}
          {watchedAt && <div>{i18nCompleted()}: {fmtDateTime(watchedAt)}</div>}
        </div>
      )}
      {sortedMilestones.length > 0 && (
        <div className="mt-1 pt-2 border-t border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            Timeline milestone
          </div>
          <div className="space-y-0.5">
            {sortedMilestones.map((m, i) => (
              <div key={`m-${m.percent ?? 'na'}-${m.at ?? i}`} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="font-semibold text-foreground">{m.percent}%</span>
                <span className="text-muted-foreground">{m.at ? fmtDateTime(m.at) : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
